import React, { useState, useEffect } from 'react'
import { FileText, Plus, Trash2 } from 'lucide-react'

interface Note {
  id: string
  content: string
  timestamp: Date
}

const QuickNotes: React.FC = () => {
  const [notes, setNotes] = useState<Note[]>([])
  const [newNote, setNewNote] = useState('')
  const [isAdding, setIsAdding] = useState(false)

  useEffect(() => {
    const savedNotes = localStorage.getItem('grace-staff-notes')
    if (savedNotes) {
      try {
        const parsedNotes = JSON.parse(savedNotes).map((note: any) => ({
          ...note,
          timestamp: new Date(note.timestamp)
        }))
        setNotes(parsedNotes)
      } catch (error) {
        console.error('Error loading notes:', error)
      }
    }
  }, [])

  useEffect(() => {
    localStorage.setItem('grace-staff-notes', JSON.stringify(notes))
  }, [notes])

  const addNote = () => {
    if (newNote.trim()) {
      const note: Note = {
        id: Date.now().toString(),
        content: newNote.trim(),
        timestamp: new Date()
      }
      setNotes([note, ...notes])
      setNewNote('')
      setIsAdding(false)
    }
  }

  const deleteNote = (id: string) => {
    setNotes(notes.filter(note => note.id !== id))
  }

  const formatTimestamp = (date: Date) => {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Quick Notes</h3>
        <button
          onClick={() => setIsAdding(true)}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {isAdding && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Type your note here..."
            className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-grace-blue-500 focus:border-transparent"
            rows={3}
            autoFocus
          />
          <div className="flex justify-end space-x-2 mt-3">
            <button
              onClick={() => {
                setIsAdding(false)
                setNewNote('')
              }}
              className="btn-secondary text-sm"
            >
              Cancel
            </button>
            <button
              onClick={addNote}
              className="btn-primary text-sm"
            >
              Save Note
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3 max-h-64 overflow-y-auto">
        {notes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p>No notes yet</p>
            <p className="text-sm">Click the + button to add a quick note</p>
          </div>
        ) : (
          notes.map((note) => (
            <div key={note.id} className="p-3 bg-gray-50 rounded-lg">
              <div className="flex justify-between items-start mb-2">
                <span className="text-xs text-gray-500">
                  {formatTimestamp(note.timestamp)}
                </span>
                <button
                  onClick={() => deleteNote(note.id)}
                  className="text-gray-400 hover:text-red-500 transition-colors"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {note.content}
              </p>
            </div>
          ))
        )}
      </div>

      {notes.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Notes are saved locally in your browser
          </p>
        </div>
      )}
    </div>
  )
}

export default QuickNotes 